import {
  Banner,
  Button,
  Page,
  TextStyle,
  RadioButton,
  ButtonGroup,
  Modal,
  TextContainer
} from '@superops/ui-design-system';
import { EventEmitter, Loader, getTranslatedText } from '@nile/components';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import {
  AccountClient,
  GET_CALENDAR_LIST,
  GET_SELECTED_CALENDAR,
  REMOVE_CALENDAR,
  SELECT_CALENDAR_TO_INTEGRATE,
  useCachedQuery
} from '@nile/graphql/src';
import { getIntegratedCalendarHook } from '@nile/components/src/Utils/helpers';

const CalendarSettings = (props) => {
  const intl = useIntl();
  const integratedCalendarData = getIntegratedCalendarHook();
  const integratedCalendarId = integratedCalendarData?.integratedCalendarId;
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [calendarList, setCalendarList] = useState([]);
  const [syncedCalendar, setSynctedCalendar] = useState({});
  const [modalActive, setModalActive] = useState(false);
  const [changeCalendarActive, setChangeCalendarActive] = useState(false);

  const toggleModal = (bool) => {
    const state = bool || !modalActive;
    setModalActive(state);
  };

  const togglCalendarChangeeModal = (bool) => {
    const state = bool || !changeCalendarActive;
    setChangeCalendarActive(state);
  };

  const { loading, data } = useQuery(GET_CALENDAR_LIST, {
    fetchPolicy: 'network-only',
    client: AccountClient,
    variables: {
      input: integratedCalendarId,
      entityName: 'UserCalendarInfo'
    },
    skip: !integratedCalendarId,
    onCompleted(res) {
      setCalendarList(res?.getTPEntityItems);
    }
  });

  const {
    loading: selectedCalendarLoading,
    data: selectedCalendarItem
  } = useCachedQuery(GET_SELECTED_CALENDAR, {
    client: AccountClient,
    variables: {
      integrationRefId: integratedCalendarId,
      entityName: 'UserCalendarInfo'
    },
    onCompleted(res) {
      const calendar = res?.getMappedTPItemInfo?.[0];
      if (calendar && !isEmpty(calendar)) {
        setSynctedCalendar(calendar);
        setSelectedCalendar(calendar);
      }
    }
  });

  const [integrateCalendar, { loading: saveLoading }] = useMutation(
    SELECT_CALENDAR_TO_INTEGRATE,
    {
      client: AccountClient
    }
  );

  const [removeCalendar, { loading: deleteLoading }] = useMutation(
    REMOVE_CALENDAR,
    {
      client: AccountClient
    }
  );

  const onConfirm = () => {
    setChangeCalendarActive(false);
    integrateCalendar({
      variables: {
        integrationRefId: integratedCalendarId,
        entityName: 'UserCalendarInfo',
        tpItemId: selectedCalendar?.id
      }
    }).then((res) => {
      EventEmitter.dispatch('calendarReset');
    });
  };

  const onLogout = () => {
    setModalActive(false);
    removeCalendar({
      variables: {
        integrationRefId: integratedCalendarId
      }
    }).then((res) => {
      EventEmitter.dispatch('calendarReset');
    });
  };

  return (
    <div id="calendar-import" className="fhp w100 flex actr jctr">
      <div id="calendar-import-page-wrapper" className="wm-600 w100">
        <Page
          title={getTranslatedText(intl, 'scheduling.calendar.settings.title')}
          primaryAction={
            <ButtonGroup>
              <Button plainSecondary onClick={props?.onCancel}>
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button
                primary
                loading={saveLoading}
                onClick={togglCalendarChangeeModal}
                disabled={
                  isEmpty(selectedCalendar) ||
                  deleteLoading ||
                  selectedCalendar?.id === syncedCalendar?.id
                }
              >
                <FormattedMessage id="common.confirm" />
              </Button>
            </ButtonGroup>
          }
        >
          <div className="mb20">
            <Banner status="info">
              <TextStyle>
                <FormattedMessage id="scheduling.calendar.settings.banner" />
              </TextStyle>
            </Banner>
          </div>
          {loading ? (
            <div className="min-height-100 flex actr jctr">
              <Loader />
            </div>
          ) : (
            <>
              {calendarList.map((el, idx) => {
                const isSynced = el?.id === syncedCalendar?.id;
                return (
                  <React.Fragment key={el?.name}>
                    <div className="flex g10 actr">
                      <div>
                        <RadioButton
                          label={el?.name}
                          checked={el?.id === selectedCalendar?.id}
                          onChange={() => setSelectedCalendar(el)}
                        />
                      </div>
                      {isSynced && (
                        <div>
                          <TextStyle variation="subdued">Synced</TextStyle>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              <div className="flex jend mt10">
                <Button
                  plain
                  onClick={toggleModal}
                  loading={deleteLoading}
                  disabled={saveLoading}
                >
                  {getTranslatedText(
                    intl,
                    'scheduling.calendar.settings.logout'
                  )}
                </Button>
              </div>
              {changeCalendarActive && (
                <Modal
                  open={changeCalendarActive}
                  onClose={() => togglCalendarChangeeModal(false)}
                  variant="confirmation"
                  title={getTranslatedText(intl, 'app.general.toast.warning')}
                  primaryAction={{
                    content: getTranslatedText(intl, 'common.confirm'),
                    onAction: onConfirm
                  }}
                  secondaryActions={[
                    {
                      content: getTranslatedText(intl, 'common.cancel'),
                      onAction: () => togglCalendarChangeeModal(false)
                    }
                  ]}
                >
                  <Modal.Section>
                    <TextContainer>
                      <FormattedMessage id="scheduling.calendar.settings.calendar.change.warning.message" />
                    </TextContainer>
                  </Modal.Section>
                </Modal>
              )}

              {modalActive && (
                <Modal
                  open={modalActive}
                  onClose={() => toggleModal(false)}
                  variant="confirmation"
                  title={getTranslatedText(intl, 'app.general.toast.warning')}
                  primaryAction={{
                    content: getTranslatedText(intl, 'common.confirm'),
                    onAction: onLogout
                  }}
                  secondaryActions={[
                    {
                      content: getTranslatedText(intl, 'common.cancel'),
                      onAction: () => toggleModal(false)
                    }
                  ]}
                >
                  <Modal.Section>
                    <TextContainer>
                      <FormattedMessage id="scheduling.calendar.settings.logout.warning.message" />
                    </TextContainer>
                  </Modal.Section>
                </Modal>
              )}
            </>
          )}
        </Page>
      </div>
    </div>
  );
};

export default CalendarSettings;
